import { createID } from "@common/helpers/funlib";
import { ng } from "@common/ngds";

// Auxillary function for getting beam length
export function getBeamLength(element: ng.element.IData, project: ng.input.IData): number {
  let beamLength = 0.0;
  if (project && project.structures && project.structures[0] && project.structures[0].nodes) {
    let nodes = project.structures[0].nodes;
    let start = nodes.find((n) => n.id?.guid === element.getBeam()?.startNodeGuid);
    let end = nodes.find((n) => n.id?.guid === element.getBeam()?.endNodeGuid);

    if (start && start.coord && start.coord.x != null && end && end.coord && end.coord.x != null) {
      beamLength = end.coord.x - start.coord.x;
    }
  }
  return beamLength;
}

// Auxillary function for getting column length
export function getColumnLength(element: ng.element.IData, project: ng.input.IData): number {
  let columnLength = 0.0;
  if (project && project.structures && project.structures[0] && project.structures[0].nodes) {
    let nodes = project.structures[0].nodes;
    let start = nodes.find((n) => n.id?.guid === element.getBeam()?.startNodeGuid);
    let end = nodes.find((n) => n.id?.guid === element.getBeam()?.endNodeGuid);

    if (start && start.coord && start.coord.x != null && end && end.coord && end.coord.x != null) {
      columnLength = end.coord.x - start.coord.x;
    }
  }
  return columnLength;
}

// Creates a default load case.
export function createDefaultLoadcase() {
  let lc = ng.loadcase.Data.create({
    id: createID("LCA #"),
    type: ng.loadcase.Type.TYPE_PERMANENT_LOAD,
    durationClass: ng.loadcase.DurationClass.DURATION_CLASS_PERMANENT,
    category: ng.loadcase.Category.CATEGORY_UNSPECIFIED,
    numberOfStoreys: 0,
    description: "",
  });

  return lc;
}

// Creates a default load combination based a given list of relevant loadcases.
// Placeholder values of 1.0 is added as partial coefficients for all load cases.
export function createDefaultLoadcombination(lcs: ng.loadcase.IData[]) {
  let lcomb = ng.loadcombination.Data.create({
    id: createID("LCO #"),
    type: ng.loadcombination.Type.TYPE_CHARACTERISTIC,
    limitState: ng.loadcombination.LimitState.LIMIT_STATE_STR,
    dependentLcombGuid: "",
    coa: ng.loadcombination.CoaType.COA_TYPE_614B,
    deactive: false,
    description: "",
    geoType: ng.loadcombination.GeoType.GEO_TYPE_UNSPECIFIED,
    kfi: 1.0,

    beamConfig: ng.loadcombination.BeamConfiguration.create({
      secondOrder: false,
      psi2: 1.0,
    }),
    userDefined: true,
    parts: [],
  });

  for (let lca of lcs) {
    let gamma = ng.loadcombination.Coefficient.create({
      type: ng.loadcombination.CoefficientType.COEFFICIENT_TYPE_GAMMA,
      value: 1.0,
    });

    let part = ng.loadcombination.CombinationPart.create({
      lcaseGuid: lca.id?.guid,
      coefficients: [gamma],
    });

    lcomb.parts.push(part);
  }

  return lcomb;
}

// Creates a default load combination part based of given loadcase.
// Placeholder values of 1.0 is added as partial coefficients for all load cases.
export function createDefaultLcombPart(lcs: ng.loadcase.IData[], lcomb: ng.loadcombination.IData) {
  // Gather all used loadcase guid in the load combination
  let allUsedLoadCases = lcomb.parts?.map((p) => {
    return p.lcaseGuid as string;
  });

  // Find the first unused loadcase (if any)
  let loadcaseId = "";
  for (let i = 0; i < lcs.length; i++) {
    if (!allUsedLoadCases?.includes(lcs[i].id?.guid as string)) {
      loadcaseId = lcs[i].id?.guid as string;
      break;
    }
  }

  // If a loadcase was found, create a new part
  if (loadcaseId !== "") {
    let gamma = ng.loadcombination.Coefficient.create({
      type: ng.loadcombination.CoefficientType.COEFFICIENT_TYPE_GAMMA,
      value: 1.0,
    });

    let part = ng.loadcombination.CombinationPart.create({
      lcaseGuid: loadcaseId,
      coefficients: [gamma],
    });

    return part;
  }

  // If no loadcase was found, return null
  return null;
}

// Creates a default stiffener.
export function createDefaultStiffener() {
  let stiff = ng.beam.Stiffener.create({
    pos: 0.0,
    lft: true,
    rgt: true,
    t: 5e-3,
  });

  return stiff;
}

// Creates a default point load.
export function createDefaultPointLoad(project: ng.input.IData, element: ng.element.IData) {
  if (
    !project ||
    !project.structures ||
    !project.structures[0] ||
    !project.structures[0].loading ||
    !project.structures[0].loading.loads ||
    !project.structures[0].loading.loadcases
  ) {
    return null;
  }

  var loads = project.structures[0].loading.loads;
  var loadcases = project.structures[0].loading.loadcases;
  let lcGuid: string = "";

  // If no loads are defined, use the first loadcase
  if (loads.length === 0) {
    if (loadcases.length === 0) {
      return null;
    }
    if (loadcases[0].id && loadcases[0].id.guid) {
      lcGuid = loadcases[0].id.guid;
    }
  }
  // If loads are defined, use the loadcase from the most recent load added
  else {
    // Find the most recent point load added and use its loadcase guid
    for (let i = loads.length - 1; i >= 0; i--) {
      const lastLoad = loads[i];
      if (
        lastLoad &&
        lastLoad.lcaseGuid &&
        lastLoad.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_POINT
      ) {
        lcGuid = lastLoad.lcaseGuid;
        break;
      }
    }

    // If no point loads were found, use the first loadcase
    if (lcGuid === "" && loadcases[0].id && loadcases[0].id.guid) {
      lcGuid = loadcases[0].id.guid;
    }
  }

  let length = getBeamLength(element, project);

  let pl = ng.load.Data.create({
    id: createID(`New point load ${loads.length + 1}`),
    type: ng.load.Type.TYPE_FORCE,
    distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_POINT,
    direction: ng.geometry.Vector3D.create({ x: -1.0, y: 0.0, z: 0.0 }),
    positions: [ng.geometry.Point3D.create({ x: length / 2.0, y: 0.0, z: 0.0 })],
    values: [10.0e3],
    lcaseGuid: lcGuid,
    assignedObjects: [element.id?.guid as string],
  });

  return pl;
}

// Creates a default line load.
export function createDefaultLineLoad(project: ng.input.IData, element: ng.element.IData) {
  if (
    !project ||
    !project.structures ||
    !project.structures[0] ||
    !project.structures[0].loading ||
    !project.structures[0].loading.loads ||
    !project.structures[0].loading.loadcases
  ) {
    return null;
  }

  var loads = project.structures[0].loading.loads;
  var loadcases = project.structures[0].loading.loadcases;
  let lcGuid: string = "";

  // If no loads are defined, use the first loadcase
  if (loads.length === 0) {
    if (loadcases.length === 0) {
      return null;
    }
    if (loadcases[0].id && loadcases[0].id.guid && loadcases[0].type !== ng.loadcase.Type.TYPE_SELF_WEIGHT) {
      lcGuid = loadcases[0].id.guid;
    }
  }
  // If loads are defined, use the loadcase from the most recent load added
  else {
    // Find the most recent line load added and use its loadcase guid
    for (let i = loads.length - 1; i >= 0; i--) {
      const lastLoad = loads[i];
      const lcase = loadcases.find((lc) => lc.id?.guid === lastLoad.lcaseGuid);
      if (lcase && lcase.type === ng.loadcase.Type.TYPE_SELF_WEIGHT) {
        continue;
      }
      if (lastLoad && lastLoad.lcaseGuid && lastLoad.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_LINE) {
        lcGuid = lastLoad.lcaseGuid;
        break;
      }
    }

    // If no line loads were found, use the first loadcase
    if (
      lcGuid === "" &&
      loadcases[0].id &&
      loadcases[0].id.guid &&
      loadcases[0].type !== ng.loadcase.Type.TYPE_SELF_WEIGHT
    ) {
      lcGuid = loadcases[0].id.guid;
    }
  }

  let length = getBeamLength(element, project);

  let ll = ng.load.Data.create({
    id: createID(`New line load ${loads.length + 1}`),
    type: ng.load.Type.TYPE_FORCE,
    distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_LINE,
    direction: ng.geometry.Vector3D.create({ x: -1.0, y: 0.0, z: 0.0 }),
    positions: [
      ng.geometry.Point3D.create({ x: 0.0, y: 0.0, z: 0.0 }),
      ng.geometry.Point3D.create({ x: length, y: 0.0, z: 0.0 }),
    ],
    values: [5.0e3, 5.0e3],
    lcaseGuid: lcGuid,
    assignedObjects: [element.id?.guid as string],
  });

  return ll;
}
